/**
 * Real-time multiplayer asteroids game with TypeScript/Bun server and HTML5 Canvas client
 * Lobby domain - Animated lobby card component
 */

import type { LobbyMetadata } from "@game/domain";
import { motion } from "framer-motion";
import type React from "react";
import { useLobbySimulation } from "./hooks/useLobbySimulation";
import { LobbyCardBody } from "./LobbyCardBody";
import { LobbyCardFooter } from "./LobbyCardFooter";
import { LobbyCardHeader } from "./LobbyCardHeader";

interface LobbyCardProps {
  lobby: LobbyMetadata;
  isFirst: boolean;
  onJoin: (lobbyId: string) => boolean;
  lobbiesList: LobbyMetadata[];
}

export const LobbyCard: React.FC<LobbyCardProps> = ({
  lobby,
  isFirst,
  onJoin,
  lobbiesList,
}) => {
  const simulation = useLobbySimulation(lobby, isFirst, lobbiesList);

  const isUrgent = isFirst && lobby.secondsUntilStart <= 5;
  const cardClasses = [
    "lobby-card",
    isFirst ? "joinable" : "",
    isUrgent ? "lobby-urgent-bounce" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger if clicking the button directly
    if ((e.target as HTMLElement).classList.contains("lobby-card-join-btn")) {
      return;
    }
    // Only allow clicking on joinable (first) cards
    if (isFirst) {
      onJoin(lobby.id);
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 252 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -252 }}
      transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
      className={cardClasses}
      data-lobby-id={lobby.id}
      onClick={handleCardClick}
      style={{ pointerEvents: "auto" }}
    >
      <LobbyCardHeader lobby={lobby} isFirst={isFirst} />
      <LobbyCardBody lobby={lobby} simulation={simulation} />
      <LobbyCardFooter lobby={lobby} isFirst={isFirst} onJoin={onJoin} />
    </motion.div>
  );
};
